// The banks of the two company forms of the client application (SEP-293).
//
// THE SOURCE IS THE FIRM'S OWN FORMS:
//
//   - `DGW Corporate App Form (Disc/Adv) v1.1 0925`, the form of a company
//     with the Discretionary Managed service or the Advisory service.
//     The text is `docs/sources/sep-293-app-company-da.txt`, and the question
//     list is `docs/sources/sep-293-company-da-questions.md`.
//   - `DGW Corporate App Form (XO) v1.0 0625`, the form of a company with the
//     Execution Only service. The text is
//     `docs/sources/sep-293-app-company-eo.txt`, and the question list is
//     `docs/sources/sep-293-company-eo-questions.md`.
//
// The rules H1 to H17 of the design apply on top of the question lists.
//
// # ONE FILE, TWO BANKS
//
// The two paper forms share most parts. The Execution Only form is the
// Discretionary and Advisory form without Part 4 (the financial status of the
// company), Part 8 (the investment objectives and the experience) and Part 9
// (the risk questions). A question with the same words in both forms has one
// id. A few words differ (the box about the representative, the description
// of the service, one example of account usage), and each bank keeps the
// words of its own form.
//
// # THE APPLICANT IS THE NOMINATED REPRESENTATIVE
//
// The person who fills in the form is the nominated representative of the
// company (Part 2). The form says that the representative "must always be
// entered as the Primary Account Contact". The representative answers under
// the ids of the individual form (rule H1), so the identity check, the
// settled email address and the key facts work unchanged. The address history
// of the representative is the address history of the individual form
// version 3.0: a move-in date for each address, up to three addresses, and 36
// months in all (a decision of the firm on SEP-293).
//
// The other persons of Part 3 (each director, and each beneficial owner with
// 25% or more) are a person group, `stakeholder_<n>_*`. Each one follows the
// paper form: a yes or no question about 3 years at the current address, and
// one previous address.
//
// # WHAT IS NOT HERE
//
//   - The signatures (Part 15 of the Discretionary and Advisory form, Part 12
//     of the Execution Only form, and the signature column of the authorised
//     signatories). The submission act replaces the signature of the
//     representative. The firm gets the other signatures off-line.
//   - Appendix I and Appendix II, the lists of documents. The investment
//     manager asks for the documents after the submission (rule H14).
//   - Declaration 6c, "Schedule of Charges" (rule H8). The firm gives the
//     Schedule of Charges with the Mandate, as for the individual form.
//
// # THE WORDING IS DRAFT
//
// Each label of the paper form is the firm's draft copy, as for the
// individual form. The online questions that the paper form does not print
// (the yes or no questions that open a person, an address or a row) are our
// own copy, and the firm settles them before the hold of the feature lifts.
//
// # THE IDS ARE FROZEN
//
// `FROZEN_CLIENT_APPLICATION_IDS_BY_FORM` in `client-application-frozen-ids.ts`
// pins the ids of each bank. A slice may add an id, and it may never drop or
// rename one.

import {
    ADDRESS_HISTORY_NEEDS_SECOND,
    ADDRESS_HISTORY_NEEDS_THIRD,
    CLIENT_APPLICATION_ADDRESS_HISTORY_RULES,
    addressHistoryNeedsSecond,
    addressHistoryNeedsThird,
} from './client-application-address-history';
import {
    CLIENT_APPLICATION_UNITED_KINGDOM,
    addressQuestions,
    individualQuestion,
    individualSectionQuestions,
    personGroup,
    questionChanges,
    rowOpenerId,
    sharedServiceTypeSection,
} from './client-application-blocks';
import { CLIENT_APPLICATION_PORTAL_SECURITY_RULE } from './client-application-portal-security';
import type {
    ClientApplicationBank,
    ClientApplicationQuestion,
    ClientApplicationSection,
    ClientApplicationStatement,
} from './client-application-questions';

/** The two variants of the company form. */
export type CompanyFormVariant = 'discretionary_advisory' | 'execution_only';

/** The version of each company form, from the footer of the paper form. */
export const COMPANY_DISCRETIONARY_ADVISORY_FORM_VERSIONS = ['1.1'] as const;
export const COMPANY_EXECUTION_ONLY_FORM_VERSIONS = ['1.0'] as const;

/** The number of person blocks of Part 3. */
export const COMPANY_STAKEHOLDER_COUNT = 5;
/** The number of rows of the table of authorised signatories. */
export const COMPANY_AUTHORISED_SIGNATORY_COUNT = 6;

/** The id prefix of the directors and the beneficial owners of Part 3. */
export const COMPANY_STAKEHOLDER_PREFIX = 'stakeholder';

/** The question that holds the capacity of the representative. */
export const APPLICANT_CAPACITY_ID = 'applicant_capacity';

// ---------------------------------------------------------------------------
// The copy that both forms print
// ---------------------------------------------------------------------------

/** The table of Appendix III of each entity form: the identifier of each nationality. */
const NATIONAL_CLIENT_IDENTIFIER_TABLE =
    'United Kingdom: UK National Insurance Number. Austria: CONCAT. Belgium: Belgian National Number (Numéro de registre national — Rijksregisternummer). Bulgaria: Bulgarian Personal Number. Czech Republic: National identification number (Rodné číslo). Germany: CONCAT. Denmark: Personal identity code 10 digits alphanumerical: DDMMYYXXXX. Estonia: Estonian Personal Identification Code (Isikukood). Finland: Personal identity code. France: CONCAT. Greece: 10 DSS digit investor share. Croatia: Personal Identification Number (OIB — Osobni identifikacijski broj). Hungary: CONCAT. Ireland: CONCAT. Iceland: Personal Identity Code (Kennitala). Italy: Fiscal code (Codice fiscale). Lithuania: Personal code (Asmens kodas). Luxembourg: CONCAT. Latvia: Personal code (Personas kods). Malta: National Identification Number. Norway: 11 digit personal ID (Foedselsnummer). Poland: National Identification Number (PESEL). Portugal: Tax number (Número de Identificação Fiscal). Romania: National Identification Number (Cod Numeric Personal). Sweden: Personal identity number. Slovenia: Personal Identification Number (EMŠO: Enotna Matična Številka Občana). Slovakia: Personal number (Rodné číslo). Spain: Tax identification number (Código de identificación fiscal). All other countries: National Passport Number.';

/**
 * Appendix III of an entity form, the explainer of each National Client
 * Identifier. `whom` names the persons that the form asks, in the words of
 * the form, for example "directors/UBOs".
 */
export function entityNationalClientIdentifierExplainer(whom: string): string {
    return [
        `To fulfil our regulatory obligations under the Markets in Financial Instruments Directive (MiFID II), we are required to confirm the nationality and National Client Identifier (NCI) of non-UK ${whom}. Your NCI will depend on your nationality. Please refer to the table below to determine the appropriate NCI, and please contact us if you need help regarding your NCI.`,
        NATIONAL_CLIENT_IDENTIFIER_TABLE,
    ].join(' ');
}

/** Appendix III of both company forms. */
const NATIONAL_CLIENT_IDENTIFIER_EXPLAINER =
    entityNationalClientIdentifierExplainer('national directors/UBOs');

/** The label of the National Client Identifier on both forms. */
const NATIONAL_CLIENT_IDENTIFIER_LABEL =
    'National Client Identifier (see Appendix III. If UK national, use NI no.)';

const NOTE_REPRESENTATIVE_DEFINITION: ClientApplicationStatement = {
    id: 'company_representatives_note_1',
    text: 'Nominated Representative (NR): a nominated representative is an individual in a company such as a director who has been nominated by their co-director(s) to make investment decisions on behalf of the company.',
};

/** The box about the representative, and the description of each service, page 1 of each form. */
function companyNotes(variant: CompanyFormVariant): readonly ClientApplicationStatement[] {
    if (variant === 'execution_only') {
        return [
            NOTE_REPRESENTATIVE_DEFINITION,
            {
                id: 'company_representatives_note_4',
                text: 'The nominated representative is also the individual responsible for receiving our correspondence and will be our primary contact for the account.',
            },
            {
                id: 'company_service_note_execution_only',
                text: 'Execution Only: Please note that Dowgate Wealth is only responsible for executing the trades as requested by you. We will not offer any investment advice or recommendations as part of this account service.',
            },
        ];
    }
    return [
        NOTE_REPRESENTATIVE_DEFINITION,
        {
            id: 'company_representatives_note_2',
            text: 'The nominated representative is the individual whom we assess with regards to suitability, specifically in relation to investment knowledge and experience. They will also be responsible for receiving our correspondence and recommendations. Depending on their level of knowledge and experience we may alter what we determine is ultimately suitable as a portfolio structure. This will be detailed in the account opening letter.',
        },
        {
            id: 'company_representatives_note_3',
            text: 'A nominated representative’s details must always be entered as the “Primary Account Contact’s” details on the application.',
        },
        {
            id: 'company_service_note_discretionary',
            text: 'Discretionary Managed service: A professional investment manager makes investment decisions for the company without needing its approval each time.',
        },
        {
            id: 'company_service_note_advisory',
            text: 'Advisory service: The company gets advice from a professional investment manager, but the company makes the final decision on its investments and execute trades itself.',
        },
    ];
}

// ---------------------------------------------------------------------------
// Part 1, the company details
// ---------------------------------------------------------------------------

function companyDetailsSection(variant: CompanyFormVariant): ClientApplicationSection {
    const registered = { questionId: 'company_registered_address_different', equals: ['Yes'] };
    return {
        id: 'company_details',
        part: 1,
        title: 'Company details',
        stepLabel: 'Company',
        notes: companyNotes(variant),
        questions: [
            { id: 'company_name', kind: 'short_text', label: 'Company name', required: true },
            {
                id: 'company_status',
                kind: 'short_text',
                label: 'Status (Ltd, Plc, LLP etc)',
                required: true,
            },
            {
                id: 'company_date_of_incorporation',
                kind: 'date',
                label: 'Date of incorporation',
                required: true,
            },
            {
                id: 'company_country_of_incorporation',
                kind: 'country_select',
                label: 'Country of incorporation',
                required: true,
            },
            { id: 'company_lei', kind: 'short_text', label: 'Legal Entity Identifier (LEI)' },
            {
                id: 'company_registration_number',
                kind: 'short_text',
                label: 'Registration number',
                required: true,
            },
            ...addressQuestions({
                prefix: 'company_correspondence_address',
                group: 'Correspondence address',
                required: true,
                houseLabel: 'Building no./name',
            }),
            {
                id: 'company_phone_number',
                kind: 'short_text',
                label: 'Phone number',
                textFormat: 'phone',
                group: 'Correspondence address',
            },
            {
                id: 'company_email',
                kind: 'short_text',
                label: 'Email',
                textFormat: 'email',
                group: 'Correspondence address',
            },
            {
                // The paper form prints "REGISTERED ADDRESS (if different to
                // Correspondence Address)" and no question. The online form
                // asks the question.
                id: 'company_registered_address_different',
                kind: 'yes_no',
                label: 'Is the registered address different to the correspondence address?',
                required: true,
                group: 'Registered address',
            },
            ...addressQuestions({
                prefix: 'company_registered_address',
                group: 'Registered address',
                required: true,
                houseLabel: 'Building no./name',
                showWhen: registered,
            }),
        ],
    };
}

// ---------------------------------------------------------------------------
// Part 2, the nominated representative (the applicant)
// ---------------------------------------------------------------------------

/** The ids of the person who fills in the form, from Part 1 of the individual form 3.0. */
const APPLICANT_IDS = [
    'title',
    'title_other',
    'surname',
    'forenames',
    'date_of_birth',
    'country_of_birth',
    'town_of_birth',
    'current_address_house_no_name',
    'current_address_street',
    'current_address_town_city',
    'current_address_county',
    'current_address_postcode',
    'current_address_country',
    'current_address_moved_in_date',
    'previous_address_house_no_name',
    'previous_address_street',
    'previous_address_town_city',
    'previous_address_county',
    'previous_address_postcode',
    'previous_address_country',
    'previous_address_moved_in_date',
    'third_address_house_no_name',
    'third_address_street',
    'third_address_town_city',
    'third_address_county',
    'third_address_postcode',
    'third_address_country',
    'third_address_moved_in_date',
    'mobile_number',
    'home_number',
    'business_number',
    'email_address',
    'country_of_nationality',
    'second_country_of_nationality',
    'national_client_identifier',
    'tax_residency_1_country',
    'tax_residency_1_tax_resident',
    'tax_residency_1_citizen',
    'tax_residency_1_tin',
    'has_tax_residency_2',
    'tax_residency_2_country',
    'tax_residency_2_tax_resident',
    'tax_residency_2_citizen',
    'tax_residency_2_tin',
    'has_tax_residency_3',
    'tax_residency_3_country',
    'tax_residency_3_tax_resident',
    'tax_residency_3_citizen',
    'tax_residency_3_tin',
    'us_person',
] as const;

/**
 * The applicant section of an entity form: the capacity of the person, then
 * Part 1 of the individual form 3.0. The National Client Identifier takes the
 * National Insurance format only for a UK national (rule H12), and it explains
 * the identifier of each other nationality.
 */
export function entityApplicantQuestions(input: {
    /** The label of the capacity question. Absent: the form asks no capacity. */
    readonly capacityLabel?: string;
    readonly nationalClientIdentifierExplainer: string;
    /** The label of the National Client Identifier, when the form names another appendix. */
    readonly nationalClientIdentifierLabel?: string;
    /**
     * `one`: the form prints one "Telephone number" box, which is
     * `mobile_number` (rule H5). Absent: mobile, home and business number.
     */
    readonly phones?: 'three' | 'one';
}): ClientApplicationQuestion[] {
    const ids =
        input.phones === 'one'
            ? APPLICANT_IDS.filter((id) => id !== 'home_number' && id !== 'business_number')
            : APPLICANT_IDS;
    return [
        ...(input.capacityLabel === undefined
            ? []
            : [
                  {
                      id: APPLICANT_CAPACITY_ID,
                      kind: 'short_text' as const,
                      label: input.capacityLabel,
                      required: true as const,
                  },
              ]),
        ...individualSectionQuestions(
            'personal_information',
            '3.0',
            ids,
            questionChanges([
                ...(input.phones === 'one'
                    ? ([['mobile_number', { label: 'Telephone number' }]] as const)
                    : []),
                [
                    'national_client_identifier',
                    {
                        label:
                            input.nationalClientIdentifierLabel ?? NATIONAL_CLIENT_IDENTIFIER_LABEL,
                        explainer: input.nationalClientIdentifierExplainer,
                        textFormat: undefined,
                        textFormatWhen: {
                            questionId: 'country_of_nationality',
                            equals: [CLIENT_APPLICATION_UNITED_KINGDOM],
                            format: 'national_insurance_number',
                        },
                    },
                ],
                ['us_person', { dropExplainer: true }],
            ]),
        ),
    ];
}

const APPLICANT_SECTION: ClientApplicationSection = {
    id: 'applicant',
    part: 2,
    title: 'Nominated representative - personal information',
    stepLabel: 'Representative',
    intro: 'This section is to be completed by the Nominated Representative who will be the main contact for the account and will make investment decisions on behalf of the company - see page 1 for more information.',
    questions: entityApplicantQuestions({
        capacityLabel:
            'Please detail your capacity as company representative eg director/shareholder',
        nationalClientIdentifierExplainer: NATIONAL_CLIENT_IDENTIFIER_EXPLAINER,
    }),
};

// ---------------------------------------------------------------------------
// Part 3, the directors and the beneficial owners
// ---------------------------------------------------------------------------

const STAKEHOLDERS_SECTION: ClientApplicationSection = {
    id: 'company_stakeholders',
    part: 3,
    title: 'Personal information',
    stepLabel: 'Directors',
    intro: 'This section is to be completed by all company directors and any ultimate beneficial owners (UBOs) with a controlling interest in the company of 25% or more. This is a regulatory requirement. If needed, please use the Additional Information section at the back of the form to add further details.',
    questions: personGroup({
        prefix: COMPANY_STAKEHOLDER_PREFIX,
        count: COMPANY_STAKEHOLDER_COUNT,
        heading: (n) => `Personal information (${n})`,
        openerLabel: 'Does the company have another director or beneficial owner to add?',
        capacityLabel:
            'Please detail your capacity as company representative eg director/shareholder',
        phones: 'three',
        nationalClientIdentifierExplainer: NATIONAL_CLIENT_IDENTIFIER_EXPLAINER,
    }),
};

// ---------------------------------------------------------------------------
// Part 4 (Discretionary and Advisory form), the financial status
// ---------------------------------------------------------------------------

function money(id: string, label: string, group: string): ClientApplicationQuestion {
    return { id, kind: 'currency', label, group };
}

function text(id: string, label: string, group: string): ClientApplicationQuestion {
    return { id, kind: 'short_text', label, group };
}

const FINANCIAL_STATUS_SECTION: ClientApplicationSection = {
    id: 'company_financial_status',
    part: 4,
    title: 'Financial status of the company',
    stepLabel: 'Finances',
    intro: 'This section is to be completed by the Nominated Representative who will be making decisions on behalf of the company.',
    questions: [
        money('company_income_investment', 'Investment income', 'Annual income'),
        money('company_income_all_other', 'All other income', 'Annual income'),
        text('company_income_all_other_sources', 'All other income source(s)', 'Annual income'),
        money(
            'company_commitment_loan_repayments',
            'Loan repayments',
            'Annual financial commitments',
        ),
        money(
            'company_commitment_other_significant_expenses',
            'Other significant expenses',
            'Annual financial commitments',
        ),
        text(
            'company_commitment_other_significant_expenses_detail',
            'Other significant expenses detail',
            'Annual financial commitments',
        ),
        money('company_asset_company_property_value', 'Company property value', 'Assets'),
        money('company_asset_other_property_value', 'Other property value', 'Assets'),
        money('company_asset_deposits', 'Company deposits', 'Assets'),
        money('company_asset_business_interests', 'Business interests', 'Assets'),
        money('company_asset_other_investments_value', 'Value of other investments', 'Assets'),
        money('company_asset_other', 'Other assets', 'Assets'),
        text('company_asset_other_detail', 'Other assets detail', 'Assets'),
        money('company_liability_tax', 'Tax', 'Liabilities'),
        money('company_liability_mortgage_outstanding', 'Mortgage outstanding', 'Liabilities'),
        money('company_liability_other_debt', 'Other debt', 'Liabilities'),
        text('company_liability_other_debt_detail', 'Other debt detail', 'Liabilities'),
    ],
};

// ---------------------------------------------------------------------------
// Health
// ---------------------------------------------------------------------------

/** The six health questions of the individual form 2.0, which each entity form prints. */
export function entityHealthQuestions(): ClientApplicationQuestion[] {
    return individualSectionQuestions('health_and_support', '2.0', [
        'general_health',
        'general_health_detail',
        'upcoming_operation',
        'upcoming_operation_detail',
        'other_health_conditions',
        'other_health_conditions_detail',
    ]);
}

/** The health section of an entity form, with the intro of the form. */
export function entityHealthSection(part: number, intro?: string): ClientApplicationSection {
    return {
        id: 'health_and_support',
        part,
        title: 'Health',
        stepLabel: 'Health',
        ...(intro === undefined ? {} : { intro }),
        questions: entityHealthQuestions(),
    };
}

function healthSection(part: number): ClientApplicationSection {
    return entityHealthSection(
        part,
        'This section is to be completed by the Nominated Representative who will be making decisions on behalf of the company.',
    );
}

// ---------------------------------------------------------------------------
// Source of wealth and funds
// ---------------------------------------------------------------------------

/** The six rows of the source table, with the prompt of each row. */
export const COMPANY_SOURCE_TYPES: ReadonlyArray<{ key: string; label: string; prompt: string }> = [
    {
        key: 'investments',
        label: 'Investments',
        prompt: 'Please provide details of how these were accumulated.',
    },
    {
        key: 'capital_raise',
        label: 'Capital raise',
        prompt: 'Please provide nature and detail of raise event e.g debt or equity.',
    },
    {
        key: 'funding_programme',
        label: 'Funding programme',
        prompt: 'Please provide nature and detail of Funding e.g. private equity, venture capital, crowdfunding, grants detailing all relevant 3rd parties.',
    },
    {
        key: 'business_property',
        label: 'Business property',
        prompt: 'Please provide details of the property(s), including address and date sold if relevant.',
    },
    {
        key: 'retained_earnings',
        label: 'Retained earnings',
        prompt: 'Please provide metrics and relevant signposting to your company Financial Statement',
    },
    {
        key: 'other',
        label: 'Other',
        prompt: 'If Source of Wealth/Funds is ‘other’, please provide further details.',
    },
];

function sourceOfWealthSection(part: number): ClientApplicationSection {
    return {
        id: 'company_source_of_wealth_and_funds',
        part,
        title: 'Source of wealth & funds',
        stepLabel: 'Wealth & funds',
        intro: 'In very simple terms, source of wealth is about how the company earned its wealth over time, while source of funds is about where the money it will be using to invest comes from. We need to understand both of these elements for a clearer picture of the company’s financial situation, so we can manage its investments and service its needs in the best way. These requirements are also part of the UK’s broader anti-money laundering regulations and help ensure the integrity of the financial system.',
        questions: [
            {
                id: 'company_source_types',
                kind: 'multi_select',
                label: 'Source Type',
                required: true,
                options: COMPANY_SOURCE_TYPES.map((row) => row.label),
                optionNotes: Object.fromEntries(
                    COMPANY_SOURCE_TYPES.map((row) => [row.label, row.prompt]),
                ),
            },
            ...COMPANY_SOURCE_TYPES.flatMap((row): ClientApplicationQuestion[] => {
                const chosen = { questionId: 'company_source_types', equals: [row.label] };
                return [
                    {
                        id: `company_source_of_wealth_${row.key}_detail`,
                        kind: 'long_text',
                        label: 'Source of Wealth detail (including country where wealth is/was generated)',
                        explainer: row.prompt,
                        required: true,
                        showWhen: chosen,
                        group: row.label,
                    },
                    {
                        id: `company_source_of_funds_${row.key}_detail`,
                        kind: 'long_text',
                        label: 'Source of Funds detail (including country where funds are/were generated)',
                        required: true,
                        showWhen: chosen,
                        group: row.label,
                    },
                ];
            }),
        ],
    };
}

// ---------------------------------------------------------------------------
// Account usage
// ---------------------------------------------------------------------------

/**
 * The twelve questions of account usage of an entity form, with the example
 * that the form prints under each yes or no question.
 */
export function entityAccountUsageQuestions(examples: {
    readonly regular: string;
    readonly capital: string;
    readonly funding: string;
}): ClientApplicationQuestion[] {
    return individualSectionQuestions(
        'account_type_and_usage',
        '3.0',
        [
            'regular_withdrawals_needed',
            'regular_withdrawals_amount',
            'regular_withdrawals_frequency',
            'regular_withdrawals_frequency_other',
            'capital_withdrawal_expected',
            'capital_withdrawal_amount',
            'capital_withdrawal_frequency',
            'capital_withdrawal_frequency_other',
            'ongoing_account_funding_expected',
            'ongoing_account_funding_amount',
            'ongoing_account_funding_frequency',
            'ongoing_account_funding_frequency_other',
        ],
        questionChanges([
            ['regular_withdrawals_needed', { explainer: examples.regular }],
            // The entity forms have no tick for "all accumulated income".
            ['regular_withdrawals_amount', { requiredUnlessTicked: undefined }],
            ['capital_withdrawal_expected', { explainer: examples.capital }],
            ['ongoing_account_funding_expected', { explainer: examples.funding }],
        ]),
    );
}

function accountUsageSection(variant: CompanyFormVariant, part: number): ClientApplicationSection {
    return {
        id: 'account_type_and_usage',
        part,
        title: 'Account usage',
        stepLabel: 'Account usage',
        intro: 'It’s important for us to understand company expectations regarding withdrawals from its portfolio, so we can plan the company’s investments accordingly. It’s also a regulatory requirement and additional protection for the company if, for example, we observe any unusual activity. This section is to be completed by the Nominated Representative.',
        questions: entityAccountUsageQuestions({
            regular:
                'For example, dividends paid out monthly or quarterly to the company bank account',
            capital: 'For example, capital is required to invest in new software or machinery',
            funding:
                variant === 'discretionary_advisory'
                    ? 'For example, transfer of company profits to your investment account'
                    : 'For example, transfer of funds from an annual bonus to your investment account',
        }),
    };
}

// ---------------------------------------------------------------------------
// Parts 8 and 9 (Discretionary and Advisory form)
// ---------------------------------------------------------------------------

const PRODUCT_ROWS = [
    'cash_deposits',
    'fixed_income',
    'equities',
    'funds',
    'alternatives',
    'derivatives',
] as const;

/**
 * The questions of Part 8 of an entity form: the objective of the entity,
 * its restrictions, and the experience of the person who fills in the form.
 * Each id is the id of the individual form 2.0 (rule H9).
 */
export function entityObjectivesQuestions(labels: {
    readonly objectiveLabel: string;
    readonly restrictionsLabel: string;
}): ClientApplicationQuestion[] {
    return [
        individualQuestion('investment_objective', '2.0', {
            label: labels.objectiveLabel,
            dropOptionNotes: true,
        }),
        individualQuestion('investment_restrictions', '2.0', {
            label: labels.restrictionsLabel,
            dropExplainer: true,
        }),
        individualQuestion('portfolio_duration', '2.0', {
            label: 'How long, if at all, have you been investing?',
            dropExplainer: true,
        }),
        individualQuestion('knowledgeable_investor', '2.0', { dropExplainer: true }),
        ...PRODUCT_ROWS.map((row) =>
            individualQuestion(`product_experience_${row}`, '2.0', {
                dropExplainer: true,
                group: 'What experience do you have with the below investment products?',
            }),
        ),
        individualQuestion('other_product_experience', '2.0', {
            dropExplainer: true,
            group: 'What experience do you have with the below investment products?',
        }),
        individualQuestion('invested_in_aim', '2.0', { dropExplainer: true }),
        individualQuestion('has_financial_qualifications', '2.0', { dropExplainer: true }),
        // The paper forms print "taken place". The online form reads "taken
        // part", as the individual form does.
        individualQuestion('taken_part_in_placing_or_new_issue', '2.0', { dropExplainer: true }),
        individualQuestion('worked_in_financial_industry', '2.0', { dropExplainer: true }),
    ];
}

const INVESTMENT_OBJECTIVES_SECTION: ClientApplicationSection = {
    id: 'investment_objectives_and_experience',
    part: 8,
    title: 'Investment objectives & experience',
    stepLabel: 'Objectives',
    intro: 'It’s important for us to understand the company’s objectives and any previous investment experience. Not only is this a UK regulatory requirement. It also helps ensure the portfolio we build for the company is suitable, and the advice and way we communicate with the company is appropriate. This section is to be completed by the Nominated Representative. As main account contact and decision maker, the nominated representative is the individual whom we assess with regards to investment experience.',
    notes: [
        {
            id: 'company_client_classification_note_1',
            text: 'To provide the best possible services and outcomes we need to assign a client classification to every client - which are investor categories defined by the financial regulator. This classification helps us understand your company’s financial knowledge and needs, ensuring we offer the right products and advice. It also ensures that we follow the necessary regulations to protect the company based on your experience and the amount of money being invested.',
        },
        {
            id: 'company_client_classification_note_2',
            text: 'Please note, we will classify the company as a retail client unless you strongly feel a professional classification is more appropriate. Please see the descriptions below and inform your company’s Investment Manager if you feel you fall outside of the retail client classification.',
        },
        {
            id: 'company_client_classification_note_3',
            text: 'Retail client: Generally small corporate clients which has limited scope for financial services, typically with less experience or knowledge of investing.',
        },
        {
            id: 'company_client_classification_note_4',
            text: 'Professional client: A company with a higher level of investment knowledge and experience, and whose career may have been in, or related to, financial markets.',
        },
    ],
    questions: entityObjectivesQuestions({
        objectiveLabel: 'What best describes the investment objective of the company?',
        restrictionsLabel:
            'If the company has any ethical, environmental, geographic, sector, or stock specific restrictions, please provide these in as much detail as possible below:',
    }),
};

/** The twelve risk questions of the individual form 2.0, with the words of the company form. */
const RISK_LABELS: ReadonlyArray<readonly [string, string]> = [
    [
        'risk_protection_over_returns',
        'With regards to this investment, I generally consider protecting the company’s investment is more important than the potential to achieve high returns:',
    ],
    [
        'risk_prefer_fewer_ups_and_downs',
        'With regards to this investment, the company would prefer fewer ups and downs in value, and would be willing to accept the lower returns this investment may make:',
    ],
    [
        'risk_comfortable_with_smaller_companies',
        'Smaller companies can achieve faster and higher growth but can also be illiquid and at greater risk of failure than FTSE 100 companies. The company would be comfortable to invest in this type of company for the potential to achieve a larger return:',
    ],
    [
        'risk_understands_smaller_company_risks',
        'I understand – and am comfortable with – the risks associated with investing in smaller companies:',
    ],
    [
        'risk_response_to_quarter_fall',
        'In the four weeks before the first lockdown (23 March 2020), the FTSE 100 fell 25% in value. If the company owned an investment that lost over 25% in four weeks, it would:',
    ],
    [
        'risk_loss_that_prompts_move_to_cash',
        'Over the short term, investments are likely to fall as well as rise. The company would begin to feel the need to cut its losses and move to cash upon a loss of:',
    ],
    [
        'risk_investment_horizon',
        'How long do you think the company will remain invested before taking its money out?',
    ],
    ['risk_income_security', 'How secure are the company’s sources of income?'],
    [
        'risk_income_change_over_ten_years',
        'Over the course of the next ten years, how do you expect the company’s level of income to change, if at all?',
    ],
    [
        'risk_liquid_funds_months_of_expenses',
        'How many months of expenses could the company’s liquid funds cover?',
    ],
    [
        'risk_capacity_for_loss',
        'How much of the company’s investments with Dowgate Wealth do you think that it is able to lose without having a significant impact on its future commitments or investment objectives?',
    ],
    [
        'risk_liabilities_change_over_ten_years',
        'Over the course of the next ten years, how do you expect the company’s liabilities to change, if at all?',
    ],
];

/**
 * The twelve risk questions of the individual form 2.0, with the words of an
 * entity form. The ids, the kinds and the options stay (rule H6).
 */
export function entityRiskQuestions(
    labels: ReadonlyArray<readonly [string, string]>,
): ClientApplicationQuestion[] {
    return labels.map(([id, label]) =>
        individualQuestion(id, '2.0', { label, dropExplainer: true }),
    );
}

const RISK_SECTION: ClientApplicationSection = {
    id: 'risk_tolerance_and_capacity_for_loss',
    part: 9,
    title: 'Risk tolerance & capacity for loss',
    stepLabel: 'Risk',
    intro: 'This section should be completed by the Nominated Representative. Whether it’s your first investment responsibility or you’re an experienced investor, grasping the relationship between risk and return is fundamental to making informed decisions. The questions in this section help us evaluate the company’s tolerance and capacity for risk, ensuring we apply the appropriate risk profile to the portfolio. You can find out more in our Risk and Investment guide and Capacity for Loss guide.',
    questions: entityRiskQuestions(RISK_LABELS),
};

// ---------------------------------------------------------------------------
// Bank account, third party, portal security
// ---------------------------------------------------------------------------

/** The seven questions of the bank account of the individual form. */
export function entityBankAccountQuestions(): ClientApplicationQuestion[] {
    return individualSectionQuestions('bank_account_information', '3.0', [
        'bank_name',
        'bank_account_name',
        'bank_sort_code',
        'bank_account_number',
        'bank_iban',
        'bank_bic_aba',
        'bank_account_currency',
    ]);
}

function bankAccountSection(part: number): ClientApplicationSection {
    return {
        id: 'bank_account_information',
        part,
        title: 'Bank account information',
        stepLabel: 'Bank account',
        intro: 'Please note that payments will only be made to and accepted from the company’s nominated bank account detailed below and that a certified copy of a bank statement will be required. For non-UK bank accounts, please provide IBAN, BIC / ABA numbers and the account currency.',
        questions: entityBankAccountQuestions(),
    };
}

/** The opener and the thirteen questions about one third party, without the representative block. */
export function entityThirdPartyQuestions(): ClientApplicationQuestion[] {
    return individualSectionQuestions('third_party_disclosure', '3.0', [
        'has_third_party',
        'third_party_title',
        'third_party_title_other',
        'third_party_surname',
        'third_party_forenames',
        'third_party_company_name',
        'third_party_address',
        'third_party_postcode',
        'third_party_email',
        'third_party_telephone',
        'third_party_role',
        'third_party_role_other',
        'third_party_correspondence',
        'third_party_correspondence_other',
    ]);
}

function thirdPartySection(part: number): ClientApplicationSection {
    return {
        id: 'third_party_disclosure',
        part,
        title: 'Third party information',
        stepLabel: 'Third party',
        intro: 'In certain situations, it is useful to have copies of certain reports and documentation relating to the company’s account(s) sent to third parties. For example a tax advisor for assessment and tax filing purposes. If you would like Dowgate Wealth to provide information regarding the account to a third-party individual, please provide the relevant details below.',
        questions: entityThirdPartyQuestions(),
    };
}

/** The seven portal security questions of the individual form. */
export function entityPortalSecurityQuestions(): ClientApplicationQuestion[] {
    return individualSectionQuestions('online_portal_security_questions', '3.0', [
        'portal_security_answer_first_car',
        'portal_security_answer_holiday_destination',
        'portal_security_answer_first_pet',
        'portal_security_answer_childhood_road',
        'portal_security_answer_favourite_colour',
        'portal_security_answer_mothers_maiden_name',
        'portal_security_answer_fathers_first_name',
    ]);
}

function portalSecuritySection(part: number): ClientApplicationSection {
    return {
        id: 'online_portal_security_questions',
        part,
        title: 'Online portal security questions',
        stepLabel: 'Portal security',
        intro: 'Our online portal gives you access to the company’s investment portfolio, so you can see its holdings, the value of investments and a history of all transactions. For security, we ask the Nominated Representative to provide answers to the following questions. Additional access can be provided upon request for other company directors or third parties.',
        questions: entityPortalSecurityQuestions(),
    };
}

// ---------------------------------------------------------------------------
// Authorised signatories, additional notes, declarations
// ---------------------------------------------------------------------------

/**
 * The rows of a table of authorised signatories: a name and a responsibility
 * in each row. Row 1 always shows. Each later row opens with a yes or no
 * question. The signature column is a wet signature, and the online form
 * does not ask it.
 */
export function authorisedSignatoryQuestions(count: number): ClientApplicationQuestion[] {
    const questions: ClientApplicationQuestion[] = [];
    for (let n = 1; n <= count; n++) {
        const group = `Authorised signatory ${n}`;
        const opener = rowOpenerId('authorised_signatory', n);
        const opens = n === 1 ? undefined : { questionId: opener, equals: ['Yes'] };
        if (n > 1) {
            questions.push({
                id: opener,
                kind: 'yes_no',
                label: 'Add another authorised signatory?',
                required: true,
                group,
                ...(n > 2
                    ? {
                          showWhen: {
                              questionId: rowOpenerId('authorised_signatory', n - 1),
                              equals: ['Yes'],
                          },
                      }
                    : {}),
            });
        }
        questions.push(
            {
                id: `authorised_signatory_${n}_name`,
                kind: 'short_text',
                label: 'Name',
                required: true,
                group,
                ...(opens === undefined ? {} : { showWhen: opens }),
            },
            {
                id: `authorised_signatory_${n}_responsibility`,
                kind: 'short_text',
                label: 'Responsibility',
                required: true,
                group,
                ...(opens === undefined ? {} : { showWhen: opens }),
            },
        );
    }
    return questions;
}

function authorisedSignatoriesSection(part: number): ClientApplicationSection {
    return {
        id: 'authorised_signatories',
        part,
        title: 'Authorised signatories',
        stepLabel: 'Signatories',
        intro: 'Please provide details and signatures of all Authorised Signatories for the investment portfolio including the individual acting as the Nominated Representative.',
        questions: authorisedSignatoryQuestions(COMPANY_AUTHORISED_SIGNATORY_COUNT),
    };
}

/**
 * The last free-text question of each entity form (rule H13). The paper form
 * sends more persons and more third parties here (page 1 of each form), and
 * `intro` gives those words.
 */
export function entityAdditionalNotesSection(intro: string): ClientApplicationSection {
    return {
        id: 'additional_notes',
        part: null,
        title: 'Additional notes',
        stepLabel: 'Notes',
        intro,
        questions: [{ id: 'additional_notes', kind: 'long_text', label: 'Additional notes' }],
    };
}

/** The additional notes of the company forms. */
const ADDITIONAL_NOTES_SECTION: ClientApplicationSection = entityAdditionalNotesSection(
    'If you need to add more information – for example additional director information or additional third-party information requirements – please use the additional notes section at the end of this form.',
);

/** The statements of the company declarations. Declaration 6c is not here (rule H8). */
const COMPANY_DECLARATIONS: readonly ClientApplicationStatement[] = [
    {
        id: 'company_declaration_1',
        text: 'I have read and understood the nature and the risk of the product(s) that the company intends to invest in through this account.',
    },
    {
        id: 'company_declaration_2',
        text: 'I act on behalf of the company as specified on this application form and not on behalf of another party in respect of all matters related to this account. Accordingly, all funds to be deposited and transacted on the account are the funds of the company.',
    },
    {
        id: 'company_declaration_3',
        text: 'I am not aware of any health or other conditions that I may have that might affect the proper operation of the account on behalf of the company.',
    },
    {
        id: 'company_declaration_4',
        text: 'I have provided true, accurate and complete information and undertake to update Dowgate Wealth of any changes to the information provided without delay.',
    },
    {
        id: 'company_declaration_5',
        text: 'I accept that in certain circumstances Dowgate Wealth will be obliged to share information with UK tax authorities, who may pass it on to other tax authorities.',
    },
    { id: 'company_declaration_6', text: 'I have received, read and understood the following:' },
    {
        id: 'company_declaration_6a',
        text: 'a. Dowgate Wealth Terms of Business - including the product risks disclosure (website link: https://dowgatewealth.co.uk/terms-of-business/)',
    },
    {
        id: 'company_declaration_6b',
        text: 'b. Conflict of Interest Policy (website link: https://dowgatewealth.co.uk/conflicts-of-interest-policy/)',
    },
    // Rule H8: "c. Schedule of Charges" is removed on purpose, as on the
    // individual form (SEP-263.39). The lettering stays a, b, d.
    {
        id: 'company_declaration_6d',
        text: 'd. I have obtained the Dowgate Wealth Order Execution Policy (website link: https://dowgatewealth.co.uk/dowgate-wealth-ltd-order-execution-policy/)',
    },
    {
        id: 'company_declaration_7',
        text: 'I consent to the Order Execution Policy and for any orders to be executed outside a regulated market or a multilateral trading facility.',
    },
    {
        id: 'company_declaration_8',
        text: 'I consent for any unexecuted limit orders not to be made public.',
    },
    {
        id: 'company_declaration_9',
        text: 'I accept and agree to be bound by the terms provided above and consent to such terms and information including future updates to these be provided to me by way of posting on the websites indicated above.',
    },
];

/**
 * The declarations of an entity form: the declarations of the form, the four
 * declarations of the nominated representative (rule H15), the consent to
 * electronic information and the marketing choices.
 */
export function entityDeclarationQuestions(input: {
    readonly statements: readonly ClientApplicationStatement[];
    /** The explainer of the declarations, for example about the other signatures. */
    readonly explainer?: string;
    readonly nominatedRepresentative?: { readonly explainer: string };
    readonly marketingExplainer: string;
}): ClientApplicationQuestion[] {
    const questions: ClientApplicationQuestion[] = [
        {
            id: 'declarations_accepted',
            kind: 'acknowledgement',
            label: 'I accept the declarations above',
            required: true,
            statements: input.statements,
            ...(input.explainer === undefined ? {} : { explainer: input.explainer }),
        },
    ];
    if (input.nominatedRepresentative !== undefined) {
        questions.push(
            individualQuestion('nominated_representative_declarations_accepted', '3.0', {
                explainer: input.nominatedRepresentative.explainer,
                dropShowWhen: true,
                group: 'Nominated representative',
            }),
        );
    }
    questions.push(
        individualQuestion('electronic_communications_consent', '3.0', {
            label: 'I Accept (please tick this box):',
        }),
        individualQuestion('marketing_company_news_and_events', '3.0', {
            explainer: input.marketingExplainer,
        }),
        individualQuestion('marketing_market_commentary_and_insights', '3.0'),
    );
    return questions;
}

const DECLARATIONS_SECTION_QUESTIONS = entityDeclarationQuestions({
    statements: COMPANY_DECLARATIONS,
    nominatedRepresentative: {
        explainer:
            'The nominated representative and the other directors of the company must also sign. We will contact you to arrange these signatures.',
    },
    marketingExplainer:
        'We offer company news and market commentary which is emailed to the Nominated Representative detailed in Section 2. If you would like to receive this, please tick this box:',
});

/** The declarations section of an entity form, with the data protection note. */
export function entityDeclarationsSection(
    part: number,
    questions: readonly ClientApplicationQuestion[],
): ClientApplicationSection {
    return {
        id: 'declarations',
        part,
        title: 'Declarations',
        stepLabel: 'Declarations',
        notes: [
            {
                id: 'data_protection_note',
                text: 'The data you provide will be processed in line with the Dowgate Wealth Privacy Policy which can be found at: https://dowgatewealth.co.uk/privacy-policy/',
            },
        ],
        questions,
    };
}

function declarationsSection(part: number): ClientApplicationSection {
    return entityDeclarationsSection(part, DECLARATIONS_SECTION_QUESTIONS);
}

// ---------------------------------------------------------------------------
// The two banks
// ---------------------------------------------------------------------------

/** The rules of each entity bank: the address history of the applicant and the portal security answers. Each answer is required. */
export const ENTITY_BANK_RULES: Pick<
    ClientApplicationBank,
    'visibilityRules' | 'formRules' | 'requireEveryAnswer'
> = {
    visibilityRules: {
        [ADDRESS_HISTORY_NEEDS_SECOND]: addressHistoryNeedsSecond,
        [ADDRESS_HISTORY_NEEDS_THIRD]: addressHistoryNeedsThird,
    },
    formRules: [
        ...CLIENT_APPLICATION_ADDRESS_HISTORY_RULES,
        CLIENT_APPLICATION_PORTAL_SECURITY_RULE,
    ],
    requireEveryAnswer: true,
};

/** The bank of the Discretionary and Advisory form, `DGW Corporate App Form (Disc/Adv) v1.1`. */
export const COMPANY_DISCRETIONARY_ADVISORY_BANK: ClientApplicationBank = {
    versions: COMPANY_DISCRETIONARY_ADVISORY_FORM_VERSIONS,
    sections: [
        sharedServiceTypeSection(),
        companyDetailsSection('discretionary_advisory'),
        APPLICANT_SECTION,
        STAKEHOLDERS_SECTION,
        FINANCIAL_STATUS_SECTION,
        healthSection(5),
        sourceOfWealthSection(6),
        accountUsageSection('discretionary_advisory', 7),
        INVESTMENT_OBJECTIVES_SECTION,
        RISK_SECTION,
        bankAccountSection(10),
        thirdPartySection(11),
        portalSecuritySection(12),
        authorisedSignatoriesSection(13),
        ADDITIONAL_NOTES_SECTION,
        declarationsSection(14),
    ],
    ...ENTITY_BANK_RULES,
};

/** The bank of the Execution Only form, `DGW Corporate App Form (XO) v1.0`. */
export const COMPANY_EXECUTION_ONLY_BANK: ClientApplicationBank = {
    versions: COMPANY_EXECUTION_ONLY_FORM_VERSIONS,
    sections: [
        sharedServiceTypeSection(),
        companyDetailsSection('execution_only'),
        APPLICANT_SECTION,
        STAKEHOLDERS_SECTION,
        healthSection(4),
        sourceOfWealthSection(5),
        accountUsageSection('execution_only', 6),
        bankAccountSection(7),
        thirdPartySection(8),
        portalSecuritySection(9),
        authorisedSignatoriesSection(10),
        ADDITIONAL_NOTES_SECTION,
        declarationsSection(11),
    ],
    ...ENTITY_BANK_RULES,
};
